/**
 * Typed client for the public API.
 *
 * Types are hand-written against the FastAPI response models rather than
 * generated, and the tradeoff is worth naming: generation from /openapi.json
 * cannot drift, but it needs the backend running at build time and puts a
 * network dependency in CI. At this size the drift risk is smaller than the
 * build-fragility risk. If these get out of step often, that calculation
 * changes and `openapi-typescript` is the answer.
 *
 * No `any` anywhere — an untyped portfolio undercuts the positioning (PRD §4.2).
 */

import { API_BASE } from "./apiBase";

/** How precise the source content actually was. Never render past it. */
export type DatePrecision = "year" | "month" | "day";

export type SkillCategory =
  "languages" | "backend" | "ai_ml" | "frontend" | "data_tools";

export type Proficiency = "working" | "proficient" | "expert";

export interface Profile {
  full_name: string;
  headline: string;
  hero_tagline: string;
  about_md: string | null;
  email: string;
  location: string | null;
  github_url: string | null;
  linkedin_url: string | null;
  /** Path or URL to the CV. Editable in the admin. */
  resume_url: string | null;
  resume_pdf_url: string | null;
  is_open_to_work: boolean;
  availability_note: string | null;
  chat_enabled: boolean;
}

export interface ProjectSummary {
  slug: string;
  title: string;
  tagline: string;
  /** 1 flagship, 2 production work, 3 coursework. Drives grouping on /projects. */
  tier: number;
  role: string | null;
  tech_stack: string[];
  is_featured: boolean;
  sort_order: number;
  repo_url: string | null;
  live_url: string | null;
  start_date: string | null;
  start_date_precision: DatePrecision | null;
  end_date: string | null;
  end_date_precision: DatePrecision | null;
  /** Formatted server-side, at the precision the content stated. */
  date_display: string | null;
  /** Uploaded through the admin. Null means the generated visual is used. */
  cover_image_url: string | null;
  cover_image_alt: string | null;
}

export interface ProjectDetail extends ProjectSummary {
  summary_md: string | null;
  problem_md: string | null;
  approach_md: string | null;
  outcome_md: string | null;
  case_study_pdf_url: string | null;
}

export interface ExperienceItem {
  slug: string;
  company: string;
  role: string;
  employment_type: string | null;
  location: string | null;
  work_mode: string | null;
  is_current: boolean;
  description_md: string | null;
  highlights: string[];
  company_url: string | null;
  skills: string[];
  date_display: string | null;
}

export interface EducationItem {
  slug: string;
  institution: string;
  degree: string;
  field_of_study: string | null;
  grade: string | null;
  description_md: string | null;
  credential_url: string | null;
  is_current: boolean;
  date_display: string | null;
}

export interface CertificationItem {
  slug: string;
  name: string;
  issuer: string;
  platform: string | null;
  credential_id: string | null;
  credential_url: string | null;
  skills: string[];
  is_featured: boolean;
  issue_date_display: string | null;
}

export interface SkillItem {
  slug: string;
  name: string;
  category: SkillCategory;
  /** Stored, never rendered as a percentage or a bar. Nobody believes "Python 92%". */
  proficiency: Proficiency;
  is_featured: boolean;
  sort_order: number;
}

export interface AchievementItem {
  slug: string;
  title: string;
  organization: string | null;
  category: string | null;
  description_md: string | null;
  media_url: string | null;
  is_featured: boolean;
  date_display: string | null;
}

export interface TestimonialItem {
  slug: string;
  name: string;
  headline: string | null;
  relationship: string | null;
  body_md: string;
  profile_url: string | null;
  is_featured: boolean;
}

export interface ChatStats {
  window_days: number;
  conversations: number;
  messages: number;
  refusals: number;
  refusal_rate: number;
  refusals_by_reason: { reason: string; count: number }[];
  median_latency_ms: number | null;
  fallback_rate: number;
  estimated_spend_usd: number;
}

export interface ApiErrorBody {
  error: {
    type: string;
    message: string;
    problems?: { field: string; message: string }[];
  };
}

/**
 * A failed request, carrying enough for the UI to say something specific.
 *
 * `isUnavailable` separates "the server is briefly down" from "you asked for
 * something that isn't there" — content/voice-guide.md wants errors that
 * explain what happened and what to do, and those two need different words.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly type: string;

  constructor(
    status: number,
    type: string,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "ApiError";
    this.status = status;
    this.type = type;
  }

  get isUnavailable(): boolean {
    return this.status === 503 || this.status === 0;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

const BASE = `${API_BASE}/api/v1/public`;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      headers: { Accept: "application/json" },
      ...init,
    });
  } catch (cause) {
    // Network failure, DNS, offline, or a sleeping free-tier backend that
    // has not woken yet. Status 0 marks "never reached the server", which
    // the UI needs to tell apart from a 404.
    throw new ApiError(
      0,
      "network_error",
      "Couldn't reach the server. It may be waking up — try again in a moment.",
      { cause },
    );
  }

  if (!response.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new ApiError(
      response.status,
      body?.error.type ?? "http_error",
      body?.error.message ?? `Request failed with ${String(response.status)}.`,
    );
  }

  return (await response.json()) as T;
}

export const api = {
  profile: () => request<Profile>("/profile"),
  projects: (params?: { featured?: boolean; tech?: string }) => {
    const search = new URLSearchParams();
    if (params?.featured !== undefined)
      search.set("featured", String(params.featured));
    if (params?.tech) search.set("tech", params.tech);
    const query = search.toString();
    return request<ProjectSummary[]>(`/projects${query ? `?${query}` : ""}`);
  },
  project: (slug: string) => request<ProjectDetail>(`/projects/${slug}`),
  projectTech: () => request<string[]>("/projects/tech"),
  experience: () => request<ExperienceItem[]>("/experience"),
  education: () => request<EducationItem[]>("/education"),
  certifications: () => request<CertificationItem[]>("/certifications"),
  skills: () => request<SkillItem[]>("/skills"),
  achievements: () => request<AchievementItem[]>("/achievements"),
  testimonials: () => request<TestimonialItem[]>("/testimonials"),
  chatStats: () => request<ChatStats>("/chat-stats"),
};

/**
 * Wake the backend before the visitor can click anything.
 *
 * PRD §11.2 names cold start as the top operational threat, and this
 * deployment is staying on free tiers — so the backend *will* sleep. Firing
 * this on first paint means the 20–50 second wake happens while someone is
 * reading the hero, not while they stare at a spinner. Failure is ignored on
 * purpose: it is a warm-up, not a dependency.
 */
export function prewarm(): void {
  void fetch(`${API_BASE}/health`, { method: "GET", cache: "no-store" }).catch(
    () => undefined,
  );
}
